import { getServerSideSplitTreatments } from "@leafly-com/split-next";
import { GetServerSidePropsContext } from "next";

import { getBrand } from "api/requests/getBrand";
import { getBrandV3Show } from "api/requests/getBrandV3Show";
import { getIsFollowing } from "api/requests/getIsFollowing";
import getSearchResults from "api/requests/getSearchResults";
import getSponsoredArticles, {
  SponsoredArticle,
} from "api/requests/getSponsoredArticles";
import { getUserLocationData } from "api/requests/getUserLocation";
import searchProducts from "api/requests/searchProducts";
import { Brand, ProductsByCategory } from "custom-types/Brand";
import { MenuItem } from "custom-types/MenuItem";
import logError from "utils/logError";
import { getCountryCodeHeader } from "utils/requestContext";
import { getSplitKey } from "utils/split/getSplitKey";

import NewBrandExperience from "./new";
import OldBrandExperience from "./old";

const BrandPage = ({
  brand,
  isFollowing,
  nearbyProducts,
  brandsNewExperienceTreatment,
  sponsoredArticles,
  initialCategory,
  productsForCategoryCarousels,
}: {
  brand: Brand;
  isFollowing: boolean;
  nearbyProducts: MenuItem[];
  sponsoredArticles: SponsoredArticle[];
  brandsNewExperienceTreatment: boolean;
  productsForCategoryCarousels: ProductsByCategory[];
  initialCategory: string;
}) => {
  return (
    <>
      {brandsNewExperienceTreatment ? (
        <NewBrandExperience
          brand={brand}
          isFollowing={isFollowing}
          nearbyProducts={nearbyProducts}
          productsForCategoryCarousels={productsForCategoryCarousels}
          sponsoredArticles={sponsoredArticles}
        />
      ) : (
        <OldBrandExperience brand={brand} initialCategory={initialCategory} />
      )}
    </>
  );
};

export const getServerSideProps = async (
  context: GetServerSidePropsContext
) => {
  const { query } = context;
  const { slug } = query;
  const overrideFlag = query?.new?.toString().toLowerCase() === "true";

  const {
    brandsNewExperience_flag: { treatment: brandsNewExperience },
  } = await getServerSideSplitTreatments(
    "brandsNewExperience_flag",
    getSplitKey(context)
  );

  const brandsNewExperienceTreatment =
    brandsNewExperience === "on" || overrideFlag;

  if (brandsNewExperienceTreatment) {
    const brand = await getBrandV3Show(String(slug));

    if (!brand) {
      return {
        notFound: true,
      };
    }

    const countryCode = getCountryCodeHeader(context);

    const { coordinates } = await getUserLocationData(countryCode, context);
    let nearbyProducts: MenuItem[] = [];

    if (coordinates && brand.brandFeatureTier === "paid") {
      const { menuItem } = await getSearchResults(
        {
          "filter[brand_slug]": brand.slug,
          "filter[max_distance]": "20",
          "filter[type]": "menu_item",
        },
        { lat: coordinates.latitude, lon: coordinates.longitude },
        20,
        countryCode,
        12,
        0,
        undefined,
        true
      );
      nearbyProducts = menuItem;
    }

    const SponsoredArticles = await getSponsoredArticles({
      id: brand.id,
      sponsorType: "brand",
    });

    const isFollowing = await getIsFollowing({
      countryCode,
      followeeId: brand?.id,
      followeeType: "brand",
    });

    const getProductsByCategory = async () => {
      if (!brand.productCategories) {
        return [];
      }

      const productPromises = brand.productCategories
        .slice(0, 3)
        .map(async (category) => {
          try {
            const { products } = await searchProducts(
              {
                filter: { brands: [brand.name], category: category.slug },
                take: 8,
              },
              countryCode,
              { lat: coordinates?.latitude, lon: coordinates?.longitude }
            );
            return products || [];
          } catch (error) {
            logError(`Error fetching products on brand detail page - ${error}`);
            return [];
          }
        });

      return Promise.all(productPromises);
    };

    const productsForCategoryCarousels = await getProductsByCategory();

    return {
      props: {
        SponsoredArticles,
        brand,
        brandsNewExperienceTreatment,
        isFollowing,
        nearbyProducts,
        productsForCategoryCarousels,
      },
    };
  } else {
    const initialCategory = query?.productCategory
      ? String(query.productCategory).toLowerCase()
      : "";
    const brand = await getBrand(String(slug));

    if (!brand) {
      return {
        notFound: true,
      };
    }
    return {
      props: {
        brand,
        initialCategory,
      },
    };
  }
};

export default BrandPage;
